// src/pages/SavedCategoryView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Copy, Download, Target, TrendingUp, AlertTriangle, Rocket, DollarSign, Users, Lightbulb, Map, Zap, BarChart, Shield } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useAuthCheck } from '../hooks/useAuthCheck';

function SavedCategoryView() {
    useAuthCheck();

    const { id, categoryKey } = useParams();
    const navigate = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [currentSection, setCurrentSection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [businessName, setBusinessName] = useState('');
    const [isSingleSection, setIsSingleSection] = useState(false);

    // Complete analysis sections mapping
    const analysisSections = [
        // Existing Business
        { title: "Current Performance", icon: <BarChart size={20} />, key: "currentPerformance", color: "blue", type: 'existing' },
        { title: "SWOT Analysis", icon: <Target size={20} />, key: "swotAnalysis", color: "green", type: 'existing' },
        { title: "Growth Opportunities", icon: <TrendingUp size={20} />, key: "growthOpportunities", color: "yellow", type: 'existing' },
        { title: "Risk Assessment", icon: <AlertTriangle size={20} />, key: "riskAssessment", color: "red", type: 'existing' },
        { title: "Operational Efficiency", icon: <Zap size={20} />, key: "operationalEfficiency", color: "emerald", type: 'existing' },
        { title: "Financial Health", icon: <DollarSign size={20} />, key: "financialHealth", color: "purple", type: 'existing' },
        { title: "Marketing Strategy", icon: <Target size={20} />, key: "marketingStrategy", color: "indigo", type: 'existing' },
        { title: "Competitive Analysis", icon: <Shield size={20} />, key: "competitiveAnalysis", color: "orange", type: 'existing' },
        { title: "Customer Analysis", icon: <Users size={20} />, key: "customerAnalysis", color: "teal", type: 'existing' },
        { title: "Technology Assessment", icon: <Zap size={20} />, key: "technologyAssessment", color: "cyan", type: 'existing' },
        { title: "Team Optimization", icon: <Users size={20} />, key: "teamOptimization", color: "pink", type: 'existing' },
        { title: "Expansion Strategy", icon: <Map size={20} />, key: "expansionStrategy", color: "gray", type: 'existing' },
        // New Idea
        { title: "Deep Market Analysis", icon: <TrendingUp size={20} />, key: "marketAnalysis", color: "blue", type: 'new' },
        { title: "Target Audience Research", icon: <Users size={20} />, key: "audienceResearch", color: "green", type: 'new' },
        { title: "Required Resources", icon: <Zap size={20} />, key: "requiredResources", color: "yellow", type: 'new' },
        { title: "Cost Estimation", icon: <DollarSign size={20} />, key: "costEstimation", color: "emerald", type: 'new' },
        { title: "Competitor Breakdown", icon: <Target size={20} />, key: "competitorBreakdown", color: "red", type: 'new' },
        { title: "Execution Strategy", icon: <Rocket size={20} />, key: "executionStrategy", color: "purple", type: 'new' },
        { title: "Unique Selling Points", icon: <Lightbulb size={20} />, key: "uniqueSellingPoints", color: "indigo", type: 'new' },
        { title: "Risk Analysis", icon: <AlertTriangle size={20} />, key: "riskAnalysis", color: "orange", type: 'new' },
        { title: "Monetization Plan", icon: <DollarSign size={20} />, key: "monetizationPlan", color: "teal", type: 'new' },
        { title: "Full Roadmap", icon: <Map size={20} />, key: "roadmap", color: "pink", type: 'new' },
        { title: "Business Growth Strategy", icon: <TrendingUp size={20} />, key: "growthStrategy", color: "cyan", type: 'new' },
        { title: "Chat About Your Idea", icon: <Users size={20} />, key: "chatSupport", color: "gray", type: 'new' },
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: { cardbg: '#dbeafe', cardborder: '#93c5fd', cardtext: '#1e40af', text: '#1e40af' },
            green: { cardbg: '#dcfce7', cardborder: '#86efac', cardtext: '#166534', text: '#166534' },
            yellow: { cardbg: '#fef9c3', cardborder: '#fde047', cardtext: '#854d0e', text: '#854d0e' },
            emerald: { cardbg: '#d1fae5', cardborder: '#6ee7b7', cardtext: '#065f46', text: '#065f46' },
            red: { cardbg: '#fee2e2', cardborder: '#fca5a5', cardtext: '#991b1b', text: '#991b1b' },
            purple: { cardbg: '#f3e8ff', cardborder: '#c4b5fd', cardtext: '#6b21a8', text: '#6b21a8' },
            indigo: { cardbg: '#e0e7ff', cardborder: '#a5b4fc', cardtext: '#3730a3', text: '#3730a3' },
            orange: { cardbg: '#ffedd5', cardborder: '#fdba74', cardtext: '#9a3412', text: '#9a3412' },
            teal: { cardbg: '#ccfbf1', cardborder: '#5eead4', cardtext: '#134e4a', text: '#134e4a' },
            pink: { cardbg: '#fce7f3', cardborder: '#f9a8d4', cardtext: '#831843', text: '#831843' },
            cyan: { cardbg: '#cffafe', cardborder: '#5eead4', cardtext: '#164e63', text: '#164e63' },
            gray: { cardbg: '#f3f4f6', cardborder: '#d1d5db', cardtext: '#374151', text: '#374151' },
        };
        return colors[color] || colors.gray;
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        fetchAnalysis();
    }, [id, categoryKey]);

    const fetchAnalysis = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await fetch(`https://insightdraft-backend.onrender.com/api/analyses/saved/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const savedAnalysis = data.analysis;
                setAnalysis(savedAnalysis);

                let parsedData = savedAnalysis.data;
                if (typeof parsedData === 'string') {
                    parsedData = JSON.parse(parsedData);
                }
                setAnalysisData(parsedData);

                // ✅ Check if single section
                const sectionCount = Object.keys(parsedData || {}).length;
                setIsSingleSection(sectionCount === 1);

                const section = analysisSections.find(s => s.key === categoryKey);
                setCurrentSection(section);
                setBusinessName(savedAnalysis.title || 'Business Analysis');

                const analysisType = savedAnalysis.type?.includes('existing') ? 'existing' : 'new';
                localStorage.setItem('analysisType', analysisType);
            }
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const formatTextForPDF = (text) => {
        if (!text) return '';
        let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        const lines = processedText.split('\n');
        let html = '';
        let i = 0;

        while (i < lines.length) {
            let trimmed = lines[i].trim();
            const nextTrimmed = (lines[i + 1] || '').trim();

            if (trimmed.includes('|') && (nextTrimmed.includes('---') || nextTrimmed.includes('==='))) {
                const tableLines = [];
                while (i < lines.length && lines[i].trim().includes('|')) {
                    tableLines.push(lines[i].trim());
                    i++;
                }
                html += buildTableHTML(tableLines);
                continue;
            }

            if (!trimmed) { html += '<div style="height:6px"></div>'; i++; continue; }

            if (trimmed.endsWith(':') && trimmed.length < 100 && !trimmed.match(/^[•*\-+\d]/)) {
                html += `<div style="font-weight:700;font-size:14px;color:#1e3a8a;margin:16px 0 6px 0;text-align:left;">${trimmed}</div>`;
                i++; continue;
            }

            const words = trimmed.split(' ');
            if (trimmed === trimmed.toUpperCase() && words.length >= 2 && words.length <= 8 && trimmed.length < 80 && !trimmed.match(/^[•*\-+\d]/)) {
                html += `<div style="font-weight:700;font-size:14px;color:#1e3a8a;margin:16px 0 6px 0;text-align:left;">${trimmed}</div>`;
                i++; continue;
            }

            const numMatch = trimmed.match(/^(\d+\.)\s+(.+)/);
            if (numMatch) {
                html += `<div style="display:flex;gap:8px;margin:8px 0;font-size:13px;text-align:left;"><span style="font-weight:700;color:#1e3a8a;min-width:20px;">${numMatch[1]}</span><span>${numMatch[2]}</span></div>`;
                i++; continue;
            }

            const bulletMatch = trimmed.match(/^[•*\-+]\s+(.+)/);
            if (bulletMatch) {
                let bc = bulletMatch[1];
                const ci = bc.indexOf(':');
                if (ci > 0 && ci < bc.length - 1) {
                    html += `<div style="display:flex;gap:8px;margin:5px 0 5px 12px;font-size:13px;text-align:left;"><span style="color:#1e3a8a;font-weight:700;">•</span><span><strong>${bc.substring(0, ci + 1)}</strong> ${bc.substring(ci + 1).trim()}</span></div>`;
                } else {
                    html += `<div style="display:flex;gap:8px;margin:5px 0 5px 12px;font-size:13px;text-align:left;"><span style="color:#1e3a8a;font-weight:700;">•</span><span>${bc}</span></div>`;
                }
                i++; continue;
            }

            const ci = trimmed.indexOf(':');
            if (ci > 0 && ci < trimmed.length - 1 && !trimmed.substring(0, ci).includes('<strong>')) {
                html += `<div style="margin:8px 0;font-size:13px;text-align:left;"><strong style="color:#1e3a8a;">${trimmed.substring(0, ci + 1)}</strong><span style="margin-left:6px;">${trimmed.substring(ci + 1).trim()}</span></div>`;
                i++; continue;
            }

            html += `<p style="margin:6px 0;font-size:13px;line-height:1.7;text-align:left;">${trimmed}</p>`;
            i++;
        }
        return html;
    };

    const buildTableHTML = (rows) => {
        if (!rows || rows.length === 0) return '';
        let t = '<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:12px;">';
        rows.forEach((row, ri) => {
            if (row.replace(/[\|\-\s=]/g, '') === '') return;
            const cells = row.split('|').map(c => c.trim()).filter(Boolean);
            if (!cells.length) return;
            const isHead = ri === 0;
            t += `<tr style="background:${isHead ? '#dbeafe' : ri % 2 === 0 ? '#fff' : '#f9fafb'}">`;
            cells.forEach(cell => {
                const tag = isHead ? 'th' : 'td';
                const s = isHead ? 'padding:9px 12px;text-align:left;font-weight:700;color:#1e3a8a;border:1px solid #93c5fd;' : 'padding:8px 12px;text-align:left;border:1px solid #e5e7eb;vertical-align:top;';
                t += `<${tag} style="${s}">${cell}</${tag}>`;
            });
            t += '</tr>';
        });
        t += '</table>';
        return t;
    };

    const renderFormattedContent = (text) => {
        if (!text) return null;

        const renderContent = (content) => {
            const lines = content.split('\n');
            const elements = [];
            let i = 0;

            while (i < lines.length) {
                const line = lines[i];
                const trimmed = line.trim();
                const nextLine = (lines[i + 1] || '').trim();

                const isTableHeader = trimmed.includes('|') && (nextLine.includes('---') || nextLine.includes('==='));

                if (isTableHeader) {
                    const tableLines = [];
                    while (i < lines.length && lines[i].trim().includes('|')) {
                        tableLines.push(lines[i].trim());
                        i++;
                    }

                    const headerCells = tableLines[0].split('|').map(c => c.trim()).filter(Boolean);
                    const dataRows = tableLines.slice(2).filter(row => row.replace(/[\|\-\s=]/g, '') !== '');

                    elements.push(
                        <div key={`table-${i}`} style={{ overflowX: 'auto', margin: '16px 0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', border: '1px solid #e5e7eb' }}>
                                <thead>
                                    <tr style={{ background: '#eff6ff', borderBottom: '2px solid #bfdbfe' }}>
                                        {headerCells.map((header, ci) => (
                                            <th key={ci} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#1e40af' }}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataRows.map((row, ri) => {
                                        const cells = row.split('|').map(c => c.trim()).filter(Boolean);
                                        return (
                                            <tr key={ri} style={{ borderBottom: '1px solid #e5e7eb', background: ri % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                                {cells.map((cell, ci) => (
                                                    <td key={ci} style={{ padding: '12px 16px', verticalAlign: 'top' }}>{cell}</td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                    continue;
                }

                if (!trimmed) {
                    elements.push(<div key={i} style={{ height: '8px' }} />);
                    i++; continue;
                }

                const hasNoBulletOrNumber = !trimmed.match(/^[•*\-+\d]/);

                if (trimmed.endsWith(':') && hasNoBulletOrNumber) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            {trimmed}
                        </div>
                    );
                    i++; continue;
                }

                const colonIdxR2 = trimmed.indexOf(':');
                const isShortLine = trimmed.length < 80;
                const hasNoPeriodAtEnd = !trimmed.endsWith('.');
                const wordsCountR2 = trimmed.split(' ').length;
                if (colonIdxR2 > 0 && colonIdxR2 === trimmed.length - 1 && isShortLine && hasNoPeriodAtEnd && wordsCountR2 <= 10) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            {trimmed}
                        </div>
                    );
                    i++; continue;
                }

                const isMostlyCaps = trimmed === trimmed.toUpperCase();
                const wordsCount = trimmed.split(' ').length;
                const isHeadingLike = isMostlyCaps && trimmed.length < 100 && wordsCount >= 2 && wordsCount <= 8 && !trimmed.includes('.') && !trimmed.includes(',');
                if (isHeadingLike && hasNoBulletOrNumber) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            {trimmed}
                        </div>
                    );
                    i++; continue;
                }

                const numberedMatch = trimmed.match(/^(\d+\.)\s+(.+)/);
                if (numberedMatch) {
                    const [, number, text] = numberedMatch;
                    elements.push(
                        <div key={i} style={{ marginBottom: '12px' }}>
                            <div style={{ fontWeight: '700', fontSize: '1.05em', display: 'flex', gap: '8px', textAlign: 'left' }}>
                                <span>{number}</span>
                                <span>{text}</span>
                            </div>
                        </div>
                    );
                    i++; continue;
                }

                const bulletMatch = trimmed.match(/^[•*\-+]\s+(.+)/);
                if (bulletMatch && bulletMatch[1].endsWith(':')) {
                    const content = bulletMatch[1];
                    elements.push(
                        <div key={i} style={{ marginBottom: '12px', marginLeft: '24px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', fontWeight: '600' }}>•</div>
                                <div style={{ flex: 1, fontWeight: '700', fontSize: '1.05em' }}>{content}</div>
                            </div>
                        </div>
                    );
                    i++; continue;
                }

                if (bulletMatch) {
                    const bulletContent = bulletMatch[1];
                    const hasColonInMiddle = bulletContent.includes(':') && !bulletContent.endsWith(':');

                    if (hasColonInMiddle) {
                        const cIdx = bulletContent.indexOf(':');
                        const before = bulletContent.substring(0, cIdx + 1);
                        const after = bulletContent.substring(cIdx + 1).trim();
                        elements.push(
                            <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '8px', marginLeft: '24px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', marginTop: '2px' }}>•</div>
                                <div style={{ flex: 1 }}><strong>{before}</strong> {after}</div>
                            </div>
                        );
                    } else {
                        elements.push(
                            <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', marginTop: '2px' }}>•</div>
                                <div style={{ flex: 1 }}>{bulletContent}</div>
                            </div>
                        );
                    }
                    i++; continue;
                }

                if (trimmed.includes(':') && !trimmed.endsWith(':')) {
                    const colonIndex = trimmed.indexOf(':');
                    const beforeColon = trimmed.substring(0, colonIndex + 1);
                    const afterColon = trimmed.substring(colonIndex + 1).trim();
                    if (afterColon) {
                        elements.push(
                            <div key={i} style={{ marginBottom: '12px' }}>
                                <div style={{ fontWeight: '700', marginBottom: '4px', textAlign: 'left' }}>{beforeColon}</div>
                                <div style={{ marginLeft: '20px', textAlign: 'left' }}>{afterColon}</div>
                            </div>
                        );
                        i++; continue;
                    }
                }

                elements.push(
                    <div key={i} style={{ marginBottom: '8px', lineHeight: '1.6', textAlign: 'left' }}>{trimmed}</div>
                );
                i++;
            }

            return <div style={{ margin: '16px 0' }}>{elements}</div>;
        };

        return renderContent(text);
    };

    const downloadAsPDF = (sectionTitle, content) => {
        try {
            const htmlContent = formatTextForPDF(content);
            const element = document.createElement('div');
            element.innerHTML = `
            <div style="padding: 40px; max-width: 800px; margin: 0 auto;">
                <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #0d53bd;">
                    <h1 style="color: #0d53bd; font-size: 28px; margin-bottom: 10px; font-weight: 700;">${sectionTitle}</h1>
                    <div style="display: flex; justify-content: center; gap: 30px; margin-top: 15px; color: #666; font-size: 14px;">
                        <div><strong>Business:</strong> ${businessName}</div>
                        <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                </div>
                <div style="font-size: 14px; line-height: 1.7; color: #444;">${htmlContent}</div>
                <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
                    <p>Generated by InsightDraft AI • Saved Analysis</p>
                </div>
            </div>`;

            const options = {
                margin: 10,
                filename: `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 800 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };
            html2pdf().set(options).from(element).save();
        } catch (error) {
            console.error('PDF error:', error);
            alert('PDF generation failed.');
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
            </div>
        );
    }

    if (!currentSection || !analysisData) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <h3 className="text-danger mb-3">Section Not Found</h3>
                    <button className="btn btn-primary" onClick={() => navigate('/my-analyses')}>Back to My Analyses</button>
                </div>
            </div>
        );
    }

    const colorClass = getColorClasses(currentSection.color);
    const content = analysisData[categoryKey] || 'No content available.';
    const formattedContent = renderFormattedContent(content);

    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            <div className="container-fluid py-3 py-md-5 px-2 px-md-4" style={{ maxWidth: 1400 }}>

                {/* Header */}
                <div className="d-flex flex-row align-items-center justify-content-between mb-3 mb-md-5 px-2">
                    {/* ✅ Responsive Back Button */}
                    <button
                        className="btn d-flex align-items-center justify-content-center"
                        onClick={() => {
                            if (isSingleSection) {
                                // Single section - no overview page, go to My Analyses
                                navigate('/my-analyses');
                            } else {
                                // Full analysis - overview page exists
                                navigate(`/saved/${id}`);
                            }
                        }}
                        style={{
                            borderRadius: '12px',
                            border: `2px solid ${colorClass?.cardtext || '#1e40af'}`,
                            padding: 'clamp(8px, 2vw, 10px) clamp(12px, 2.5vw, 20px)',
                            fontSize: 'clamp(14px, 3vw, 16px)',
                            height: 'clamp(40px, 8vw, 44px)',
                            minWidth: 'clamp(40px, 10vw, 120px)',
                            color: colorClass?.cardtext || '#1e40af',
                            backgroundColor: 'transparent',
                            flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <span className="d-none d-xl-inline-flex align-items-center">
                            <ArrowLeft size={18} className="me-2" />
                            {isSingleSection ? 'Back to Saved' : 'Back to Overview'}
                        </span>
                        <span className="d-none d-lg-inline-flex d-xl-none align-items-center">
                            <ArrowLeft size={16} className="me-1" />
                            {isSingleSection ? 'Saved' : 'Overview'}
                        </span>
                        <span className="d-none d-md-inline-flex d-lg-none align-items-center">
                            <ArrowLeft size={16} />
                            <span className="ms-1">Back</span>
                        </span>
                        <span className="d-inline-flex d-md-none align-items-center justify-content-center" title={isSingleSection ? 'Back to Saved' : 'Back to Overview'}>
                            <ArrowLeft size={16} />
                        </span>
                    </button>

                    {/* Title - Centered, takes remaining space */}
                    <div className="text-center mx-2" style={{ flex: 1 }}>
                        <h1 className="fw-bold mb-1" style={{
                            color: colorClass?.cardtext || '#1e40af',
                            fontSize: 'clamp(1rem, 4vw, 2rem)',
                            lineHeight: '1.2',
                            wordBreak: 'break-word'
                        }}>
                            {currentSection.title}
                        </h1>
                        <p className="text-muted m-0 d-none d-sm-block" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)' }}>
                            Saved Analysis: <strong style={{ color: colorClass?.text }}>"{businessName}"</strong>
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="px-0 px-md-3">
                    <div className="mx-auto" style={{ maxWidth: '100%' }}>
                        {/* ✅ Reduced padding from p-3 p-md-5 to p-2 p-md-3 */}
                        <div className="p-2 p-md-3" style={{ background: '#f8fafc' }}>
                            <div className="p-3 p-md-4 rounded-3" style={{
                                background: 'white',
                                border: window.innerWidth > 768 ? `1px solid ${colorClass?.cardborder || '#93c5fd'}` : 'none',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                boxShadow: window.innerWidth > 768 ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                            }}>
                                <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: '1.7', color: '#374151', padding: '4px', textAlign: 'left' }}>
                                    {formattedContent}
                                </div>
                            </div>
                        </div>

                        {/* ✅ Reduced footer padding from p-3 p-md-4 to p-2 p-md-3 */}
                        <div className="p-2 p-md-3 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3" style={{ background: 'white', borderTop: window.innerWidth > 768 ? '1px solid rgba(0,0,0,0.1)' : 'none' }}>
                            <div className="d-flex align-items-center gap-2 gap-md-3">
                                <small className="text-muted d-flex align-items-center" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                                    <Calendar size={window.innerWidth > 576 ? 16 : 14} className="me-1 me-md-2" />
                                    {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </small>
                                <small className="text-muted d-none d-sm-block">InsightDraft AI</small>
                            </div>

                            <div className="d-flex gap-2 gap-md-3 ms-md-auto">
                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{ borderRadius: '8px', background: colorClass?.cardbg || '#dbeafe', color: colorClass?.cardtext || '#1e40af', border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`, fontSize: 'clamp(13px, 2vw, 15px)' }}
                                    onClick={() => copyToClipboard(content)}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = colorClass?.cardtext || '#1e40af'; e.currentTarget.style.color = 'white'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe'; e.currentTarget.style.color = colorClass?.cardtext || '#1e40af'; }}
                                >
                                    <Copy size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {copied ? 'Copied!' : (window.innerWidth > 576 ? 'Copy Text' : 'Copy')}
                                </button>

                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{ borderRadius: '8px', background: colorClass?.cardbg || '#dbeafe', color: colorClass?.cardtext || '#1e40af', border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`, fontSize: 'clamp(13px, 2vw, 15px)' }}
                                    onClick={() => downloadAsPDF(currentSection.title, content)}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = colorClass?.cardtext || '#1e40af'; e.currentTarget.style.color = 'white'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe'; e.currentTarget.style.color = colorClass?.cardtext || '#1e40af'; }}
                                >
                                    <Download size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {window.innerWidth > 576 ? 'Download PDF' : 'PDF'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SavedCategoryView;